<template>
  <div class="card shadow-sm">
    <div
      class="card-header text-start fw-bold fs-5 d-flex justify-content-between align-items-center"
    >
      <div>{{ getStep.index + 1 }}. {{ getStep.title }}</div>
      <div>
        <span
          class="badge rounded-pill shadow-sm text-black"
          :class="[
            getStep.backgroundColor,
            { 'border border-secondary': !getStep.backgroundColor }
          ]"
        >
          {{ getStep.section }}
        </span>
      </div>
    </div>
    <div class="card-body">
      <div class="row">
        <div class="col-sm text-start">
          <div v-if="getStep.type === 'complete'">
            <CertificateOfCompletion />
          </div>

          <div v-else>
            <div v-if="getStep.type === 'input'">
              <FormInput
                v-bind="getStep"
                :isCorrect="
                  checkStepAnswer(getStep.value, getStep) && !getStep.openAnswer
                "
                :isSubmitted="isSubmitted"
                :isReport="isReport"
                :formInputValue="getStep.value || ''"
                @updateFormValue="updateFormValue(getStep.id, $event)"
              />
            </div>

            <div v-if="getStep.type === 'select'">
              <FormSelect
                :id="getStep.id"
                :content="getStep.content"
                :options="getStep.options"
                :isCorrect="checkStepAnswer(getStep.value, getStep)"
                :isSubmitted="isSubmitted"
                :formInputValue="getStep.value || null"
                :shuffle="getStep.shuffle"
                :isReport="isReport"
                @updateFormValue="updateFormValue(getStep.id, $event)"
              />
            </div>

            <div v-if="getStep.type === 'table'">
              <FormTable
                :id="getStep.id"
                :content="getStep.content"
                :columns="getStep.columns"
                :formInputValue="getStep.value || []"
                @tableChanged="resetSubmitted()"
                @updateTableValue="updateFormValue(getStep.id, $event)"
              />
            </div>

            <div v-if="getStep.type === 'file'">
              <FormFile
                :id="getStep.id"
                :content="getStep.content"
                :subtype="getStep.subtype"
                :fileURL="getStep.value || null"
                @updateFileURL="updateFormValue(getStep.id, $event)"
              />
            </div>

            <div
              class="text-end mt-3"
              v-if="
                (!checkStepAnswer(getStep.value, getStep) ||
                  getStep.openAnswer) &&
                !isReport
              "
            >
              <button
                class="btn btn-primary fw-bold"
                :disabled="
                  checkStepAnswer(getStep.value, getStep) && !getStep.openAnswer
                "
                @click="handleSubmit()"
              >
                Submit
              </button>
            </div>

            <!-- GOAL: Make this its own component or based from a computed to use one custom alert -->
            <div v-if="!isReport">
              <div
                v-if="
                  isSubmitted ||
                  (checkValue(getStep.value) && !unsavedChanges) ||
                  isInvalid ||
                  unsavedChanges
                "
                class="mt-4"
              >
                <CustomAlert v-if="isInvalid" type="danger" class="mb-0">
                  Please enter a valid answer before submitting.
                </CustomAlert>
                <CustomAlert
                  v-else-if="isSubmitted && getStep.openAnswer"
                  type="success"
                  class="mb-0"
                >
                  {{ savedFeedback }}
                </CustomAlert>
                <CustomAlert
                  v-else-if="!isSubmitted && unsavedChanges"
                  type="warning"
                  class="mb-0"
                >
                  You have unssuccessaved changes.
                </CustomAlert>
                <CustomAlert v-else-if="checkStepAnswer()" type="" class="mb-0">
                  {{ correctFeedback }}
                </CustomAlert>
                <CustomAlert v-else type="danger" class="mb-0">
                  {{ incorrectFeedback }}
                </CustomAlert>
              </div>
            </div>

            <div v-if="isDebugMode && !getCurrentStep.openAnswer">
              <CustomAlert class="mt-3" title="Debug" type="warning" icon="">
                Answer: {{ getStep.answer }}
              </CustomAlert>
            </div>
          </div>
        </div>

        <div class="col-sm" v-if="getStep.image.length > 0">
          <img
            :src="getStep.image"
            :alt="getStep.imageAlt"
            class="img-fluid rounded shadow-sm border border-dark"
          />
        </div>
      </div>
    </div>
    <div v-if="0">
      <div class="text-start">
        StepValues:
        <pre>{{ getStepValues }}</pre>
      </div>
      <div class="text-start">
        Current step:
        <pre>{{ getCurrentStep }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import DatastoreAPI from "@/helpers/DatastoreAPI.js";
import { mapGetters, mapActions } from "vuex";
import content from "@/data/content.json";
import FormInput from "@/components/activity/FormInput.vue";
import FormSelect from "@/components/activity/FormSelect.vue";
import FormTable from "@/components/activity/FormTable.vue";
import FormFile from "@/components/activity/FormFile.vue";
import CertificateOfCompletion from "@/components/ui/CertificateOfCompletion.vue";
export default {
  name: "PromptContainer",
  mixins: [DatastoreAPI],
  components: {
    FormInput,
    FormSelect,
    FormTable,
    FormFile,
    CertificateOfCompletion
  },
  data() {
    return {
      isSubmitted: false,
      unsavedChanges: false,
      isInvalid: false,
      isSaving: false,
      answerValue: null
    };
  },
  props: {
    step: {
      type: Object,
      default: null,
      required: false
    },
    isReport: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  computed: {
    ...mapGetters([
      "isDebugMode",
      "getStepValues",
      "getCurrentStep",
      "getCurrentStepId",
      "checkStepAnswer",
      "mapToDatastore"
    ]),
    getStep() {
      if (this.step) {
        return this.step;
      } else {
        return this.getCurrentStep;
      }
    },
    correctFeedback() {
      return this.getStep.correctFeedback || content.correctFeedback;
    },
    incorrectFeedback() {
      return this.getStep.incorrectFeedback || content.incorrectFeedback;
    },
    savedFeedback() {
      return content.savedFeedback;
    },
    checkLocalAnswerValue() {
      const value = this.answerValue;

      if (value === undefined || value === null) return false;

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === "string") {
        return value.trim().length > 0;
      }

      return true;
    }
  },
  methods: {
    ...mapActions(["setStepData", "goToStep"]),
    updateFormValue(fieldId, value) {
      this.isSubmitted = false;
      this.isInvalid = false;

      setTimeout(() => {
        if (!this.isSaving) {
          this.unsavedChanges = true;
        }
      }, 500);

      this.answerValue = value;
      console.log("Answer value updated:", this.answerValue);
    },
    handleSubmit() {
      this.unsavedChanges = false;
      this.isSaving = true;

      // Check that local answer value is valid and that it matches any step answer conditions before saving
      if (
        this.checkValue(this.answerValue) &&
        this.checkStepAnswer(this.answerValue, this.getStep)
      ) {
        this.isSubmitted = true;

        this.setStepData({
          id: this.getStep.id,
          value: this.answerValue
        });

        // Update the current step id in vuex
        if (this.step) {
          this.goToStep({ direction: 1, id: this.getStep.id });
        }

        this.updateEntry(this.mapToDatastore);
        //.then() { issSaving = false }

        // replace timeout with callback from updateEntry
        // from here
        setTimeout(() => {
          this.isSaving = false;
        }, 1000);
        //----- to here
      } else {
        this.isInvalid = true;
      }
    },
    // General value check to make sure its defined and not empty
    checkValue(value) {
      if (value === undefined || value === null) return false;

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === "string") {
        return value.trim().length > 0;
      }

      return true;
    },
    resetSubmitted() {
      this.isSubmitted = false;
    }
  },
  watch: {
    getCurrentStepId() {
      if (this.checkValue(this.getCurrentStep.value)) {
        this.isSubmitted = true;
        this.unsavedChanges = false;
        this.answerValue = this.getCurrentStep.value;
      } else {
        this.isSubmitted = false;
        this.unsavedChanges = false;
        this.answerValue = null;
      }
    }
  }
  /* lifecycle hooks */
};
</script>
